import { ErrorType } from '../../../HttpError';

/**
 * Facturas del acreedor (profesional) y su relación con los pagos (contrato §12.2, CashFlow).
 * CashFlow es dueño de la relación factura ↔ pago (varias a varias) y de los comprobantes de pago.
 */

/** De dónde viene la factura: subida en el estado de cuenta o factura de liquidación ya subida. */
export enum CreditorInvoiceSourceEnum {
    Uploaded = 1,
    SettlementFile = 2,
}

/** Estado calculado por CashFlow: 1 sin pago, 2 pagada parcialmente, 3 pagada, 4 asociada (sin importe para comparar). */
export enum CreditorInvoiceStatusEnum {
    Unpaid = 1,
    PartiallyPaid = 2,
    Paid = 3,
    Linked = 4,
}

/** Un pago que cubre (total o parcialmente) la factura. */
export interface CreditorInvoicePayment {
    paymentId: number;
    paymentDate?: string | Date | null;
    receiver?: string | null;
    /** En moneda de la factura. */
    appliedAmount?: number | null;
}

/** GET cashflow/creditors/{CreditorId}/invoices */
export interface CreditorInvoice {
    id: number;
    creditorId: string;
    settlementId?: number | null;
    source: CreditorInvoiceSourceEnum;
    settlementFileId?: number | null;
    /** Con tenant. */
    blobPath: string;
    /** Relativa al tenant (para la SAS de lectura). */
    path?: string | null;
    fileName: string;
    fileExtension: string;
    fileSize?: number | null;
    invoiceNumber?: string | null;
    invoiceDate?: string | Date | null;
    amount?: number | null;
    currencyCode?: string | null;
    comments?: string | null;
    /** Suma de lo aplicado por los pagos (moneda de la factura). */
    appliedAmount?: number | null;
    status?: CreditorInvoiceStatusEnum | number | null;
    payments?: CreditorInvoicePayment[] | null;
    created?: string | Date | null;
}

/** Datos editables de una factura. */
export interface CreditorInvoiceData {
    settlementId?: number | null;
    invoiceNumber?: string | null;
    invoiceDate?: string | null;
    amount?: number | null;
    currencyCode?: string | null;
    comments?: string | null;
}

/** POST cashflow/creditors/{CreditorId}/invoices */
export interface NewCreditorInvoice extends CreditorInvoiceData {
    path: string;
    source: CreditorInvoiceSourceEnum;
    settlementFileId?: number | null;
}

/** Una asignación (factura cubierta por el pago). */
export interface PaymentInvoiceAllocationInput {
    creditorInvoiceId: number;
    /** En moneda de la factura; vacío = asociada sin importe. */
    appliedAmount?: number | null;
}

/** GET cashflow/payments/{PaymentId}/invoices: asignaciones activas con datos de la factura. */
export interface PaymentInvoiceAllocation {
    id?: number;
    paymentId: number;
    creditorInvoiceId: number;
    appliedAmount?: number | null;
    invoice?: CreditorInvoice | null;
    invoiceNumber?: string | null;
    invoiceDate?: string | Date | null;
    amount?: number | null;
    currencyCode?: string | null;
    fileName?: string | null;
    settlementId?: number | null;
}

/** Resultado de PUT cashflow/payments/{PaymentId}/invoices */
export interface SetPaymentInvoicesResult {
    allocations: PaymentInvoiceAllocation[];
    /** Avisos (no errores): p. ej. lo aplicado supera el importe de la factura. */
    warnings: string[];
}

/** Factura de liquidación todavía no registrada como factura del profesional. */
export interface SettlementInvoiceCandidate {
    settlementId: number;
    settlementFileId: number;
    /** Relativa al tenant. */
    path: string;
    fileName: string;
    fileExtension: string;
    uploadedAt?: string;
}

// ── Pago nuevo: facturas y comprobantes que se agregan después de crearlo ──

export interface PaymentAttachmentsRequest {
    paymentId: number;
    /** Profesional acreedor: titular de la carpeta de comprobantes. */
    creditorId: string;
    /** undefined = no tocar las facturas del pago. */
    allocations?: PaymentInvoiceAllocationInput[];
    receipts: File[];
}

export interface PaymentReceiptUploadOutcome {
    fileName: string;
    file: File;
    status: 'uploaded' | 'unregistered' | 'failed';
    relativePath?: string;
    error?: ErrorType;
}

export interface PaymentAttachmentsOutcome {
    paymentId: number;
    allocations?: { status: 'saved' | 'failed'; warnings: string[]; error?: ErrorType };
    receipts: PaymentReceiptUploadOutcome[];
}

// ── Estado ──

export type CreditorInvoicesStatus = 'idle' | 'loading' | 'loaded' | 'failed' | 'unavailable';

export interface CreditorInvoicesEntry {
    status: CreditorInvoicesStatus;
    data: CreditorInvoice[] | undefined;
    error?: ErrorType;
}
